import SearchBar from "@src/lib/ui/SearchBar";
import Fuse from "fuse.js";
import React, { useMemo, useState } from "react";
import Spacer from "@src/lib/ui/Spacer";
import { Checkbox } from "@src/lib/ui/Checkbox";
import Text from "@src/lib/ui/Text";

type Props<T extends string> = {
  allChoices: Choice<T>[];
  selectedChoices: Choice<T>[];
  onSelectedChoicesChange: (newChoices: Choice<T>[]) => void;
  loading?: boolean;
  maxMenuHeight?: React.CSSProperties["maxHeight"];
  removeClearButton?: boolean;
  searchEnabled?: boolean;
  autoFocus?: boolean;
};

const fuseConfig = {
  keys: ["label"],
  threshold: 0.1,
};

export const MultiSelectMenuContent = <T extends string>({
  allChoices,
  selectedChoices,
  onSelectedChoicesChange,
  loading,
  maxMenuHeight = 240,
  removeClearButton,
  searchEnabled = true,
  autoFocus = false,
}: Props<T>): React.ReactElement => {
  const [search, setSearch] = useState("");
  const fuse = useMemo(() => {
    return new Fuse(allChoices, fuseConfig);
  }, [allChoices]);
  const searchResults = useMemo(() => {
    let filteredGroups = allChoices;
    if (search.length > 0) {
      filteredGroups = fuse.search(search).map((result) => result.item);
    }
    return filteredGroups;
  }, [fuse, search, allChoices]);
  const selectedChoiceValues = useMemo(
    () => selectedChoices.map((c) => c.value),
    [selectedChoices],
  );
  return (
    <div
      className="flex w-[240px] flex-col"
      style={{ maxHeight: maxMenuHeight }}
    >
      {searchEnabled && (
        <div className="p-2">
          <SearchBar
            className="w-full rounded-xl bg-action-secondary text-s-v4"
            inputClassName="rounded-xl w-full text-s-v4"
            placeholder={`Search...`}
            value={search}
            onChange={setSearch}
            autoFocus={autoFocus}
          />
        </div>
      )}
      <div className="h-px w-full bg-border-primary" />
      <div className="max-h-[196px] overflow-y-auto rounded-lg">
        {allChoices.length === 0 && loading && (
          <div className="ml-5 py-3.5 text-s-v4 text-text-tertiary">
            Loading...
          </div>
        )}
        {allChoices.length > 0 && searchResults.length === 0 && (
          <div className="ml-5 py-3.5 text-s-v4 text-text-tertiary">
            No results
          </div>
        )}
        {searchResults.map((choice) => (
          <label
            key={choice.value}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 hover:bg-state-hover"
          >
            <Checkbox
              checked={selectedChoiceValues.includes(choice.value)}
              onCheckedChange={() => {
                const newSelectedChoices = [...selectedChoices];
                const index = selectedChoiceValues.indexOf(choice.value);
                if (index === -1) {
                  newSelectedChoices.push(choice);
                } else {
                  newSelectedChoices.splice(index, 1);
                }
                onSelectedChoicesChange(newSelectedChoices);
              }}
            />
            <Text variant="compact-description">{choice.label}</Text>
          </label>
        ))}
        <Spacer size={searchEnabled ? 16 : 4} />
      </div>
      {!removeClearButton && allChoices.length > 0 && (
        <div className="flex h-10 min-h-[40px] items-center justify-end border-t border-border-primary">
          <button
            type="button"
            className="mr-3 cursor-pointer rounded-md text-s-v4 text-action-primary outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-brand-border"
            onClick={() => onSelectedChoicesChange([])}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiSelectMenuContent;

export type Choice<T extends string | boolean = string> = {
  value: T;
  label: string;
  type?: string;
};
